import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["aria-expanded", "aria-controls", "aria-label"]
const _hoisted_2 = { class: "wc-menu-dropdown-modern__value" }
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = ["aria-selected", "onClick"]
const _hoisted_5 = {
  class: "wc-menu-dropdown-modern__check",
  "aria-hidden": "true"
}
const _hoisted_6 = { class: "wc-menu-dropdown-modern__label" }

import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { v4 as uuid } from 'uuid';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';

interface DropdownOption {
  value: string | number | object;
  label: string;
  selected?: boolean;
}

const DROPDOWN_OPEN_EVENT = 'wc-dropdown-open';


export default /*@__PURE__*/_defineComponent({
  __name: 'WCMenuDropdownModern',
  props: {
    options: {},
    label: { default: '' },
    ariaLabel: { default: '' },
    variant: { default: '' },
    overlayMenu: { type: Boolean, default: false }
  },
  emits: ["select"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const panelId = `wc-menu-dropdown-modern-panel-${uuid()}`;
const root = ref<HTMLElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const dropUp = ref(false);
const panelPositionVars = ref<Record<string, string>>({});

const closeDropdown = (): void => {
  isOpen.value = false;
  panelPositionVars.value = {};
};

const updatePanelPosition = (): void => {
  const anchor = props.overlayMenu ? root.value?.parentElement : root.value;
  if (!anchor || !panel.value) return;
  const rect = anchor.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  dropUp.value = spaceBelow < panel.value.offsetHeight && spaceAbove > spaceBelow;
  panelPositionVars.value = {
    '--wc-dd-left': `${rect.left}px`,
    '--wc-dd-width': `${rect.width}px`,
    '--wc-dd-top': `${rect.bottom}px`,
    '--wc-dd-bottom': `${viewportHeight - rect.top}px`,
  };
};

const toggleDropdown = async (): Promise<void> => {
  if (isOpen.value) {
    closeDropdown();
    return;
  }
  isOpen.value = true;
  document.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT, { detail: panelId }));
  await nextTick();
  updatePanelPosition();
};

const selectOption = (option: DropdownOption): void => {
  emit('select', option.value);
  closeDropdown();
};

const onDocumentClick = (event: MouseEvent): void => {
  if (root.value && !root.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

const onScroll = (event: Event): void => {
  if (root.value?.contains(event.target as Node)) return;
  closeDropdown();
};

const onOtherDropdownOpen = (event: Event): void => {
  if ((event as CustomEvent<string>).detail !== panelId) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener(DROPDOWN_OPEN_EVENT, onOtherDropdownOpen);
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener(DROPDOWN_OPEN_EVENT, onOtherDropdownOpen);
  window.removeEventListener('scroll', onScroll, { capture: true });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["wc-menu-dropdown-modern", _ctx.variant ? `wc-menu-dropdown-modern--${_ctx.variant}` : null]),
    ref_key: "root",
    ref: root,
    style: _normalizeStyle(panelPositionVars.value)
  }, [
    _createElementVNode("button", {
      type: "button",
      class: "wc-menu-dropdown-modern__trigger",
      onClick: _withModifiers(toggleDropdown, ["stop"]),
      "aria-expanded": isOpen.value ? 'true' : 'false',
      "aria-controls": isOpen.value ? panelId : null,
      "aria-label": _ctx.ariaLabel
    }, [
      _renderSlot(_ctx.$slots, "trigger", {
        label: _ctx.label,
        isOpen: isOpen.value
      }, () => [
        _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.label), 1),
        _cache[0] || (_cache[0] = _createTextVNode()),
        _createVNode(WCSvgIcon, {
          name: "chevron-down",
          class: "wc-menu-dropdown-modern__chevron",
          "icon-size": "sm"
        })
      ])
    ], 8, _hoisted_1),
    _cache[2] || (_cache[2] = _createTextVNode()),
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "panel",
          ref: panel,
          id: panelId,
          class: _normalizeClass(["wc-menu-dropdown-modern__panel", {
        'wc-menu-dropdown-modern__panel--up': dropUp.value,
        'wc-menu-dropdown-modern__panel--fixed': _ctx.overlayMenu,
      }])
        }, [
          _createElementVNode("ul", {
            class: "wc-menu-dropdown-modern__list",
            "aria-label": _ctx.ariaLabel
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (option, index) => {
              return (_openBlock(), _createElementBlock("li", {
                key: index,
                class: "wc-menu-dropdown-modern__item"
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "wc-menu-dropdown-modern__action",
                  "aria-selected": option.selected ? 'true' : null,
                  onClick: _withModifiers(($event: any) => (selectOption(option)), ["stop"])
                }, [
                  _renderSlot(_ctx.$slots, "option", {
                    option: option,
                    selected: !!option.selected
                  }, () => [
                    _createElementVNode("span", _hoisted_5, [
                      (option.selected)
                        ? (_openBlock(), _createBlock(WCSvgIcon, {
                            key: 0,
                            name: "tick-icon",
                            class: "wc-menu-dropdown-modern__tick"
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _cache[1] || (_cache[1] = _createTextVNode()),
                    _createElementVNode("span", _hoisted_6, _toDisplayString(option.label), 1)
                  ])
                ], 8, _hoisted_4)
              ]))
            }), 128))
          ], 8, _hoisted_3)
        ], 2))
      : _createCommentVNode("", true)
  ], 6))
}
}

})